import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { BUSINESS } from "@/lib/business";
import { cn } from "@/lib/utils";
import Logo from "@/components/site/Logo";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/call-out", label: "Call-Out" },
  { to: "/areas", label: "Areas" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const primaryPhone = BUSINESS.phones[0];

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-100 bg-white/85 backdrop-blur" data-testid="site-header">
      <div className="container-x flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link" aria-label="iCrack Solutions home">
          <Logo size={44} />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => cn(
                "px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                isActive ? "text-white" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
              )}
              style={({ isActive }) => isActive ? { background: "var(--icrack-magenta)" } : {}}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a href={`tel:${primaryPhone.tel}`} data-testid="header-call-button">
            <Button variant="outline" className="h-10 rounded-xl border-slate-200">
              <Phone className="h-4 w-4 mr-2" style={{ color: "var(--icrack-magenta)" }} />
              {primaryPhone.display}
            </Button>
          </a>
          <Link to="/book" data-testid="header-book-button">
            <Button className="h-10 rounded-xl btn-primary-magenta">Book Repair</Button>
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <a href={`tel:${primaryPhone.tel}`} data-testid="header-call-button-mobile" aria-label="Call">
            <Button size="icon" variant="outline" className="h-10 w-10 rounded-xl border-slate-200">
              <Phone className="h-4 w-4" style={{ color: "var(--icrack-magenta)" }} />
            </Button>
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button size="icon" variant="outline" className="h-10 w-10 rounded-xl border-slate-200" data-testid="header-menu-button">
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-6 flex flex-col gap-1">
                {navLinks.map(l => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => cn(
                      "px-3 py-3 text-base font-medium rounded-xl",
                      isActive ? "text-white" : "text-slate-700 hover:bg-slate-100"
                    )}
                    style={({ isActive }) => isActive ? { background: "var(--icrack-magenta)" } : {}}
                    data-testid={`nav-drawer-${l.label.toLowerCase()}`}
                    end={l.to === "/"}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <a href={BUSINESS.whatsapp.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} data-testid="drawer-whatsapp-button">
                    <Button variant="outline" className="w-full h-11 rounded-xl"><MessageCircle className="h-4 w-4 mr-2 text-green-600"/>WhatsApp</Button>
                  </a>
                  <Link to="/book" onClick={() => setOpen(false)} data-testid="drawer-book-button">
                    <Button className="w-full h-11 rounded-xl btn-primary-magenta">Book Repair</Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
